import { useState, useEffect, createContext, ReactNode } from "react";

import { LayoutSplashScreen } from "@/../_metronic/layout/core";

import { HealthUnit } from "@/@types/esus/HealthUnit";
import { User } from "@/@types/app/user";
import { Account } from "@/@types/app/Account";
import { FederalUnit } from "@/@types/app/FederalUnit";
import { getCurrentUserAccount } from "@/services/app/userAccountService";
import { login as userLogin, logout as userLogout } from "@/services/auth/accountService";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";
import { Module } from "@/@types/app/Module";
import { registerUserAccess } from "@/services/app/userAuditLog/registerUserAccess";
import { registerCityContextChanged } from "@/services/app/userAuditLog/registerCityContextChanged";

type CurrentAccountContextType = {
	currentHealthUnit: HealthUnit;
	currentAccount: Account;
	currentFederalUnit: FederalUnit;
	currentModule: Module;
	isLoading: Boolean;
	user: User;
	getCurrentAccount: (
		module?: ModuleEnum,
		uf?: string,
		ibgeCode?: string,
		cnes?: string
	) => Promise<void>;
	uf: string;
	ibgeCode: string;
	cnes: string;
	module?: ModuleEnum;
	permissions: string[];
	login: (identifier: string, password: string) => Promise<boolean>;
	logout: () => Promise<void>;
};

export const CurrentAccountContext = createContext({} as CurrentAccountContextType);

type CurrentAccountContextProviderProps = {
	children: ReactNode;
};

export function CurrentAccountContextProvider({ children }: CurrentAccountContextProviderProps) {
	const [currentModule, setCurrentModule] = useState<Module>({} as Module);
	const [currentFederalUnit, setCurrentFederalUnit] = useState<FederalUnit>({} as FederalUnit);
	const [currentAccount, setCurrentAccount] = useState<Account>({} as Account);
	const [currentHealthUnit, setCurrentHealthUnit] = useState<HealthUnit>({} as HealthUnit);
	const [permissions, setPermissions] = useState<string[]>([]);
	const [user, setUser] = useState<User>({} as User);
	const [isLoading, setIsLoading] = useState(true);
	const [uf, setUf] = useState(localStorage.getItem("uf") || "");
	const [ibgeCode, setIbgeCode] = useState(localStorage.getItem("ibgeCode") || "");
	const [cnes, setCnes] = useState(localStorage.getItem("cnes") || "");
	const selectedModule = localStorage.getItem("module")
		? ModuleEnum[ModuleEnum[Number(localStorage.getItem("module"))] as keyof typeof ModuleEnum]
		: undefined;
	const [module, setModule] = useState(selectedModule);

	async function getCurrentAccount(
		currentModuleId?: ModuleEnum,
		currentUf?: string,
		currentIbgeCode?: string,
		currentCnes?: string
	) {
		const currentAccountWasUpdated = !(
			localStorage.getItem("module") === currentModuleId?.valueOf().toString() &&
			localStorage.getItem("uf") === currentUf &&
			localStorage.getItem("ibgeCode") === currentIbgeCode &&
			localStorage.getItem("cnes") === currentCnes
		);
		const currentAccountIsEmpty = !(
			Boolean(currentModuleId) ||
			Boolean(currentUf) ||
			Boolean(currentIbgeCode) ||
			Boolean(currentCnes)
		);
		setUf(currentUf || "");
		setIbgeCode(currentIbgeCode || "");
		setCnes(currentCnes || "");
		setModule(currentModuleId || undefined);
		localStorage.setItem("uf", currentUf || "");
		localStorage.setItem("ibgeCode", currentIbgeCode || "");
		localStorage.setItem("cnes", currentCnes || "");
		localStorage.setItem("module", currentModuleId?.valueOf().toString() || "");
		const data = await getCurrentUserAccount(
			currentUf,
			currentIbgeCode,
			currentCnes,
			currentModuleId
		);
		if (data.isAuthenticated) {
			const {
				userAccount,
				permissions: userPermissions,
				currentCityLicense,
				currentModule,
			} = data;
			setCurrentModule(currentModule || ({} as Module));
			setCurrentFederalUnit(userAccount?.federalUnit || ({} as FederalUnit));
			setUser(userAccount?.user || ({} as User));
			setCurrentHealthUnit(userAccount?.healthUnit || ({} as HealthUnit));
			if (userAccount?.account && currentCityLicense) {
				userAccount.account.currentCityLicense = currentCityLicense;
				setCurrentAccount(userAccount.account);
			}
			localStorage.setItem(
				"sintegradaServerApiUrl",
				userAccount?.account?.sIntegradaApiUrl ?? ""
			);
			setPermissions(userPermissions || []);
			if (currentAccountWasUpdated && !currentAccountIsEmpty) {
				registerCityContextChanged({
					uf: currentUf,
					ibgeCode: currentIbgeCode,
					cnes: currentCnes,
				});
			}
		} else {
			logout();
		}
	}

	async function login(identifier: string, password: string) {
		const tokens = await userLogin(identifier, password);
		if (tokens) {
			localStorage.removeItem("uf");
			localStorage.removeItem("ibgeCode");
			localStorage.removeItem("cnes");
			localStorage.removeItem("module");
			localStorage.removeItem("sintegradaServerApiUrl");
			localStorage.setItem(
				"tokens",
				JSON.stringify({
					accessToken: tokens.accessToken,
					refreshToken: tokens.refreshToken,
				})
			);

			registerUserAccess();
			await getCurrentAccount();
		}
		return Boolean(tokens);
	}

	async function logout() {
		userLogout();
		setModule(undefined);
		setUf("");
		setIbgeCode("");
		setCnes("");
		setUser({} as User);
		setCurrentModule({} as Module);
		setCurrentFederalUnit({} as FederalUnit);
		setCurrentAccount({} as Account);
		setCurrentHealthUnit({} as HealthUnit);
		localStorage.removeItem("uf");
		localStorage.removeItem("ibgeCode");
		localStorage.removeItem("cnes");
		localStorage.removeItem("module");
	}

	useEffect(() => {
		async function fetchData() {
			await getCurrentAccount(module, uf, ibgeCode, cnes);
			setIsLoading(false);
		}

		fetchData();
		return () => setCurrentAccount({} as Account);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<CurrentAccountContext.Provider
			value={{
				currentModule,
				currentFederalUnit,
				currentAccount,
				currentHealthUnit,
				isLoading,
				user,
				getCurrentAccount,
				module,
				uf,
				ibgeCode,
				cnes,
				permissions,
				login,
				logout,
			}}
		>
			{isLoading ? <LayoutSplashScreen /> : <>{children}</>}
		</CurrentAccountContext.Provider>
	);
}
